import { Component, Input } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, startWith, Subject, switchMap, tap } from 'rxjs';
import { IGetSlotResponse } from 'src/app/cabinet/models/slot';
import { ICreateSlotProductRequest, IGetSlotProductResponse, IUpdateSlotProductRequest } from 'src/app/cabinet/models/slot-product';
import { SlotProductService } from 'src/app/cabinet/services/slot-product.service';
import { CompanyService } from 'src/app/company/services/company.service';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { IProduct } from 'src/app/products/models/product';
import { ProductService } from 'src/app/products/services/product.service';
import { SlotProductValidator } from 'src/app/shared/validators/slot-product-validator';

import { SlotProductStore } from 'src/app/stores/component/slot-product-store';

@Component({
    selector: 'app-slot-product-view-admin',
    templateUrl: './slot-product-view-admin.component.html',
    styleUrl: './slot-product-view-admin.component.scss',
    providers: [SlotProductStore],
    standalone: false
})
export class SlotProductViewAdminComponent {

  ////////////////
  // variables //
  //////////////

  @Input() public slot: IGetSlotResponse;
  @Input() isAdmin: boolean;
  @Input() expandedSlotIds: string[] = [];

  public isLoading: boolean;
  public skip = 0;
  public slotProductForm: UntypedFormGroup;

  public rowClass = (context: RowClassArgs) => {
    return { 'inactive-product': !context.dataItem.product?.isActive };
  };

  slotProductOpened: boolean = false;
  editingSlotProduct: IGetSlotProductResponse;
  companyId: string;
  tenantId: string;
  currentSlotId: string;

  public searchString$ = new Subject<string>();
  public productSearchIsLoading$ = new BehaviorSubject<boolean>(false);

  vm$ = combineLatest([
    this.slotProductStore.slotProducts$,
    this.slotProductStore.loading$,
        this.searchString$.pipe(
          filter(x => x != null && x != ''),
          tap(x => this.productSearchIsLoading$.next(true)),
          debounceTime(500),
          switchMap(searchString => {
            const request: IBaseSearchRequest = {
              keyword: searchString,
              pageNumber: 1,
              pageSize: 25
            }
            return this.productService.listProducts(request)
          }),
          map(x => x.data.filter(p => p.isActive)),
          tap(x => this.productSearchIsLoading$.next(false)),
          startWith([])
        ),
        this.productSearchIsLoading$,
  ]).pipe(
    map(([
      slotProducts,
      loading,
      searchResults,
      productSearchIsLoading
    ]) => ({
      slotProducts,
      loading,
      searchResults,
      productSearchIsLoading
    }))
  );

  constructor(
    private slotProductStore: SlotProductStore,
    private slotProductService: SlotProductService,
    private productService: ProductService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {}

  public ngOnInit(): void {
    if (this.isAdmin) {
      this.getTenant();
    }
    else{
      this.initForm();
    }

    // load slot products of expanded slot
    this.slotProductStore.getSlotProductsBySlotId(this.slot.id);

  }

  // Get tenantId by company from url
  getTenant() {
    this.route.paramMap.subscribe(params => {
      this.companyId = params.get('companyId');
      this.companyService.getCompany(this.companyId)
      .subscribe(response => {
        this.tenantId = response.tenantId;
        this.initForm();
      })
    });
  }

  // delete slot product
  deleteSlotProduct(dataItem: IGetSlotProductResponse) {
    this.slotProductStore.deleteSlotProduct(dataItem.id);
  }

  // edit dialog
  public openSlotProductDialog(dataItem?: IGetSlotProductResponse): void {
    this.slotProductOpened = true;
    this.editingSlotProduct = dataItem;

    if (dataItem?.targetStock) {
      this.slotProductForm.get('targetStock').setValue(dataItem.targetStock);
    }
  }

  public closeSlotProductDialog(status: string): void {
    this.slotProductOpened = false;
    this.editingSlotProduct = null;
    this.slotProductForm.reset();
  }

  submitSlotProductUpdate() {
    if (this.editingSlotProduct) {
      // Edit existing note
      const updateRequest: IUpdateSlotProductRequest = {
        id: this.editingSlotProduct.id,
        targetStock: this.slotProductForm.value.targetStock,
      };
      this.slotProductStore.updateSlotProduct(updateRequest);
    }
    
    this.closeSlotProductDialog('no');
  }

  editSlotProduct(slot: IGetSlotProductResponse): void {
    this.slotProductOpened = true;
    
    this.editingSlotProduct = slot || null;

    if (slot) {
      // Populate the form with existing note data for editing
      this.slotProductForm.patchValue({
        productId: slot.product.id,
        targetStock: slot.targetStock,
      });

      this.onFilter(slot.product.internalNumber);
    } else {
      // Reset form for creating a new note
      this.slotProductForm.reset();
    }
  }

  removeSlotProduct(): void {
    this.slotProductStore.deleteSlotProduct(this.editingSlotProduct.id);
    this.closeSlotProductDialog('no');
  }

  submitSlotProductCreation() {
    if (this.editingSlotProduct) {
      // Edit existing slot product
      const updateRequest: IUpdateSlotProductRequest = {
        id: this.editingSlotProduct.id,
        targetStock: this.slotProductForm.value.targetStock,
      };
      this.slotProductStore.updateSlotProduct(updateRequest);
    } else {
      // Create new slot product
      const request: ICreateSlotProductRequest = {
        slotId: this.slot.id,
        productId: this.slotProductForm.value.productId,
        targetStock: this.slotProductForm.value.targetStock,
        tenantId: this.tenantId
      };
      this.slotProductStore.createSlotProduct(request);
    }
    this.closeSlotProductDialog('no');
  }

  // validateProduct(productId: string) {
  //   const productControl = this.slotProductForm.get('productId');
  
  //   if (productControl) {
  //     // Avoid triggering valueChanges again
  //     productControl.setAsyncValidators(
  //       SlotProductValidator(this.slotProductService, productId, this.tenantId)
  //     );
  
  //     // Validate only if status is not already in progress
  //     if (productControl.status !== 'PENDING') {
  //       productControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  //     }
  //   }
  // }
  

  // Fetch products based on search term
  onFilter(keywordInput: string): void {  
    this.searchString$.next(keywordInput);
  }

  initForm() {
    this.slotProductForm = this.fb.group({
      id: new UntypedFormControl(null),
      productId: new UntypedFormControl (
        null,{
          asyncValidators: 
        SlotProductValidator(this.slotProductService, this.tenantId)
        }
      ),
      targetStock: new UntypedFormControl (1, {
        validators: Validators.required
      }),
    });

    // Listen for productId changes and validate only when it actually changes
    // this.slotProductForm.get('productId')?.valueChanges
    //   .pipe(distinctUntilChanged())
    //   .subscribe(value => {
    //     if (value) {
    //       this.validateProduct(value);
    //     }
    // });
  }
}
